import React, { useContext } from 'react';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { SearchBar } from 'src/pages/CatalogPage/components/CatalogHead/SearchBar/SearchBar';
import { AuctionEndCountdown } from
  'src/pages/CatalogPage/components/CatalogHead/AuctionEndCountdown/AuctionEndCountdown';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);


import { useOverlayTriggerState } from '@react-stately/overlays';
import { OverlayContainer } from '@react-aria/overlays';
import { Modal } from '@riac/design-system';
import { AuctionSummary } from 'src/pages/CatalogPage/components/AuctionSummary/AuctionSummary';

import { TextButton } from '@riac/design-system';
import { CatalogHeadContainer,
  AuctionDateTime,
  AuctionTitle,
  AuctionSummaryToggle,
} from 'src/pages/CatalogPage/components/CatalogHead/CatalogHead.style';
import { ICatalogSearch } from 'src/pages/CatalogPage/CatalogPage.i';
import { EBookDownload } from 'src/components/EBookDownload/EBookDownload';
import { AuctionLocation } from 'src/pages/AuctionsPage/AuctionsPage.style';

interface ICatalogHead {
  excerpt: string;
  startTime: string;
  startDate: string;
  auctionTypeID: number;
  keywords: string;
  lotNumber: string;
  updateSearch: ICatalogSearch;
  complete: boolean;
  searchPostData: any;
  sessions: Array<object>;
  locationAddress: string;
  locationCity: string;
  locationState: string;
  locationZip: string;
  onlyLotsBidOn: boolean;
}

export const CatalogHead = (props: ICatalogHead): JSX.Element => {
  const { auctionStartDate, auctionEndDate, auctionEndTime,
    auctionCloseRate, lotExtensionTime, auctionTitle, auctionSubtitle, auctionTypeId, auctionId,
    auctionLocCity, auctionLocState,
  } = useContext(
    CatalogContext
  );

  const auctionSummaryModalState = useOverlayTriggerState({});

  let whenIsIt:string;
  const auctionEnded = dayjs() > auctionEndDate.add(1, 'day');
  const endTime = auctionEndTime ? dayjs(auctionEndTime, 'HH:mm:ss').format('h:mm A') : '6:00 PM';
  const endHours = auctionEndTime ? parseInt(dayjs(auctionEndTime, 'HH:mm:ss').format('H')) : 18;

  if(auctionTypeId === TIMED_AUCTION_TYPE_ID) {
    whenIsIt
      = `Ending at ${auctionEndDate.format('MMMM DD, YYYY')}, ${endTime} `
      + `${auctionEndDate.format('z')}*`;
  } else {
    whenIsIt = auctionStartDate.format('MMMM DD, YYYY');
    if(!auctionStartDate.isSame(auctionEndDate, 'day')) {
      whenIsIt += ` to ${auctionEndDate.format('MMMM DD, YYYY')}`;
    }
    if(!auctionEnded) {
      whenIsIt += ` | Starting at ${dayjs(props.startTime, 'HH:mm:ss').format('h:mm A')}`;
      if(!auctionStartDate.isSame(auctionEndDate, 'day')) {
        whenIsIt += ' Each Day';
      }
    }
  }

  return (
    <CatalogHeadContainer>
      <AuctionTitle>{auctionTitle}</AuctionTitle>
      {auctionSubtitle
        && <h3>{auctionSubtitle}</h3>
      }
      <AuctionLocation>{`${auctionLocCity}, ${auctionLocState}`}</AuctionLocation>
      <AuctionDateTime>
        <p>{whenIsIt}</p>
        {auctionTypeId === TIMED_AUCTION_TYPE_ID && (
          <>
            <AuctionEndCountdown endTime={auctionEndDate.add(endHours, 'hour')} />
            <p>
              * Each lot ends individually, {auctionCloseRate} second{auctionCloseRate > 1 ? 's' : ''} apart.
              New bids will extend each lot by {lotExtensionTime} minute{lotExtensionTime > 1 ? 's' : ''}.
            </p>
          </>
        )}
      </AuctionDateTime>
      {dayjs() > auctionEndDate.add(1, 'day') ? (
        <h3>
          You are viewing a finished auction. This auction ended on&nbsp;
          {auctionEndDate.format('MMMM DD, YYYY')}.
        </h3>
      ) : null}
      <>
        <EBookDownload
          auctionId={auctionId}
          searchPostData={props.searchPostData}
          title = {`Catalog #${auctionId}`}
          label = 'Catalog'
          pdfTitle = {`Catalog #${auctionId}`}
        />
      </>
      { ((props.auctionTypeID == 1) || (props.auctionTypeID == 2) || (auctionId === 2094))
        && <AuctionSummaryToggle>
          <TextButton
            blueText
            onPress = {() => {auctionSummaryModalState.open();}}
          >
            View Auction and Preview Dates/Times, and Lot Index
          </TextButton>
          {auctionSummaryModalState.isOpen
            && <OverlayContainer>
              <Modal
                isOpen
                onClose = {() => {auctionSummaryModalState.close();} }
                showCloseButton
                isDismissable
                title='To Be Sold At Auction'
              >
                <AuctionSummary
                  auctionStartTime = {props.startTime}
                  auctionStartDate = {props.startDate}
                  auctionTypeID = {props.auctionTypeID}
                  auctionExcerpt = {props.excerpt}
                  sessions = {props.sessions}
                  locationAddress={props.locationAddress}
                  locationCity={props.locationCity}
                  locationState={props.locationState}
                  locationZip={props.locationZip}
                />
              </Modal>
            </OverlayContainer>
          }
        </AuctionSummaryToggle>
      }
      <SearchBar
        key={`${props.keywords} ${props.lotNumber} ${props.complete}`}
        keywords={props.keywords}
        lotNumber={props.lotNumber}
        updateSearch={props.updateSearch}
        complete={props.complete}
        onlyLotsBidOn={props.onlyLotsBidOn}
      />
    </CatalogHeadContainer>
  );
};
